"use client";

import Link from "next/link";
import { useState } from "react";
import { useSession } from "next-auth/react";
import { AlertTriangle, HeartHandshake, Trash2, UserRound } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { cn } from "~/lib/utils";
import { TherapyRelationshipType } from "~/prisma/browser";

type WarningViewerRole = "client" | "therapist";

interface MessageBubbleProps {
  isMine?: boolean;
  senderName: string;
  senderImage?: string | null;
  children: React.ReactNode;
  therapyType?: TherapyRelationshipType;
  avatarVariant?: "default" | "assistant" | "user";
  onDelete?: () => void;
  isDeleting?: boolean;
  warning?: { viewerRole: WarningViewerRole };
}

const WARNING_HELP_LINKS: Record<WarningViewerRole, string> = {
  client: "/podrska/klijenti/komunikacija-na-platformi",
  therapist: "/podrska/terapeuti/komunikacija-na-platformi",
};

export function MessageBubble({
  isMine,
  senderName,
  senderImage,
  children,
  therapyType,
  avatarVariant = "default",
  onDelete,
  isDeleting,
  warning,
}: MessageBubbleProps) {
  const { data } = useSession();
  const [showConfirm, setShowConfirm] = useState(false);

  const myUsername = data?.user?.name ?? data?.user?.email ?? senderName;
  const avatarName = isMine ? myUsername : senderName;
  const avatarSrc = isMine ? (data?.user?.image ?? senderImage) : senderImage;

  const initials = avatarName
    .split(" ")
    .map((chunk) => chunk[0])
    .join("");

  const avatarElement = (
    <div className="shrink-0">
      <Avatar className="h-8 w-8">
        {avatarSrc ? (
          <AvatarImage alt={avatarName} src={avatarSrc} />
        ) : (
          <AvatarFallback
            className={cn(
              avatarVariant === "assistant"
                ? "bg-secondary text-brand-action"
                : avatarVariant === "user"
                  ? "bg-brand/10 text-brand"
                  : "text-xs",
            )}
          >
            {avatarVariant === "assistant" ? (
              <HeartHandshake className="h-4 w-4" aria-hidden="true" />
            ) : avatarVariant === "user" ? (
              <UserRound className="h-4 w-4" aria-hidden="true" />
            ) : (
              initials
            )}
          </AvatarFallback>
        )}
      </Avatar>
    </div>
  );

  return (
    <>
      <div className={cn("group flex gap-2", isMine && "justify-end")}>
        <div
          className={cn(
            "flex max-w-[88%] items-start gap-2 sm:max-w-[80%]",
            isMine && "flex-row-reverse",
          )}
        >
          {avatarElement}
          <div
            className={cn(
              "overflow-hidden",
              isMine
                ? "min-w-14 rounded-2xl rounded-tr-md bg-brand-action text-brand-foreground"
                : "rounded-2xl rounded-tl-md border border-border bg-secondary shadow-xs",
            )}
          >
            {warning && (
              <div
                role="note"
                className={cn(
                  "flex items-center gap-1 border-b border-b-amber-300 bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-900",
                )}
              >
                <AlertTriangle
                  className="h-3.5 w-3.5 shrink-0"
                  aria-hidden="true"
                />
                <span>Narušavanje pravila.</span>
                <Link
                  href={WARNING_HELP_LINKS[warning.viewerRole]}
                  className="underline underline-offset-2"
                >
                  Saznaj više
                </Link>
              </div>
            )}
            <div className="px-3 py-2">
              {!isMine && therapyType === TherapyRelationshipType.COUPLES && (
                <p className="text-xs font-semibold text-muted-foreground">
                  {senderName}
                </p>
              )}
              <div className="wrap-break-word text-sm">{children}</div>
            </div>
          </div>
          {onDelete && (
            <button
              type="button"
              onClick={() => setShowConfirm(true)}
              disabled={isDeleting}
              className="shrink-0 self-center text-muted-foreground/0 transition-colors hover:text-destructive group-hover:text-muted-foreground/50 md:text-muted-foreground/0"
              aria-label="Obriši poruku"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Obriši poruku</AlertDialogTitle>
            <AlertDialogDescription>
              Da li želiš da obrišeš ovu poruku? Ova radnja se ne može
              poništiti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Otkaži</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete?.();
                setShowConfirm(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Obriši
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
